import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import contentUsageNotice from '../config/contentUsageNotice';
import { apiFetch } from '../utils/apiFetch';

// General content-concern intake is intentionally distinct from a claim that
// VCS has made a DMCA determination. date_of_change: 2026-08-21

const LegalAndSourcesPage = () => {
  const [report, setReport] = useState({
    reportType: 'copyright',
    reporterName: '',
    reporterEmail: '',
    workDescription: '',
    materialLocation: '',
    statement: '',
    signature: ''
  });
  const [reportStatus, setReportStatus] = useState({ state: 'idle', message: '' });

  const updateReport = (field) => (event) => setReport((current) => ({ ...current, [field]: event.target.value }));
  const submitReport = async (event) => {
    event.preventDefault();
    setReportStatus({ state: 'saving', message: 'Submitting report…' });
    try {
      const response = await apiFetch('/api/legal/copyright-reports', {
        method: 'POST',
        body: JSON.stringify(report)
      });
      if (!response.ok) throw new Error(`The report could not be submitted (${response.status}).`);
      const result = await response.json();
      setReportStatus({ state: 'saved', message: `Report received. Case ${result.caseId}.` });
    } catch (error) {
      setReportStatus({ state: 'error', message: error?.message || 'The report could not be submitted.' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-5xl flex-col gap-8">
        <div className="flex flex-col gap-4 rounded-3xl border border-amber-400/20 bg-slate-900/80 p-8 shadow-2xl shadow-black/30">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="max-w-3xl space-y-3">
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-amber-300">legal & sources</p>
              <h1 className="text-4xl font-semibold text-white">{contentUsageNotice.pageTitle}</h1>
              <p className="text-base leading-7 text-slate-300">{contentUsageNotice.pageIntro}</p>
            </div>
            <div>
              <Link
                to="/"
                className="inline-flex items-center rounded-full border border-white/20 px-5 py-2.5 text-sm font-medium text-white transition hover:border-white/40 hover:bg-white/10"
              >
                Back to dashboard
              </Link>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300">product guardrails</p>
            <ul className="mt-5 space-y-4 text-sm leading-7 text-slate-200">
              {contentUsageNotice.productGuardrails.map((item) => (
                <li key={item} className="rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3">
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-6">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-sky-300">trademarks</p>
              <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.trademarkNotice}</p>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-violet-300">SRD-backed material</p>
              <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.srdNotice}</p>
              <div className="mt-5 space-y-4">
                {contentUsageNotice.srdAttributions.map((attribution) => (
                  <article key={attribution.title} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                    <h2 className="text-sm font-semibold text-white">{attribution.title}</h2>
                    <p className="mt-2 text-xs leading-6 text-slate-300">{attribution.statement}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs">
                      <a className="text-violet-300 underline decoration-violet-400/50 underline-offset-4" href={attribution.sourceUrl} target="_blank" rel="noreferrer">Read the source</a>
                      <a className="text-violet-300 underline decoration-violet-400/50 underline-offset-4" href={attribution.licenseUrl} target="_blank" rel="noreferrer">Read the license</a>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-300">references & citations</p>
              <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.referenceNotice}</p>
            </div>
          </section>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-rose-300">user-provided content</p>
            <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.userContentNotice}</p>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-cyan-300">private play &amp; sharing</p>
            <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.privatePlayNotice}</p>
          </section>
        </div>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-orange-300">responsibility is not permission</p>
          <p className="mt-4 text-sm leading-7 text-slate-200">{contentUsageNotice.prohibitedUseNotice}</p>
          <p className="mt-4 text-sm leading-7 text-slate-400">{contentUsageNotice.legalAdviceNotice}</p>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-7">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-rose-300">report material</p>
            <h2 className="mt-3 text-2xl font-semibold text-white">Copyright, trademark, or other content concern</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">Provide enough information to identify the work and the exact VCS material. Submission records an intake case; it is not an automatic legal determination.</p>
          </div>
          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={submitReport}>
            <label className="grid gap-2 text-sm text-slate-200">Report type
              <select className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" value={report.reportType} disabled={reportStatus.state === 'saving'} onChange={updateReport('reportType')}>
                <option value="copyright">Copyright</option>
                <option value="trademark">Trademark</option>
                <option value="other">Other content concern</option>
              </select>
            </label>
            <label className="grid gap-2 text-sm text-slate-200">Your name
              <input className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" value={report.reporterName} maxLength="200" required disabled={reportStatus.state === 'saving'} onChange={updateReport('reporterName')} />
            </label>
            <label className="grid gap-2 text-sm text-slate-200">Your email
              <input className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" type="email" value={report.reporterEmail} maxLength="320" required disabled={reportStatus.state === 'saving'} onChange={updateReport('reporterEmail')} />
            </label>
            <label className="grid gap-2 text-sm text-slate-200">Material location
              <input className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" type="url" placeholder="A VCS page or package URL" value={report.materialLocation} maxLength="2000" required disabled={reportStatus.state === 'saving'} onChange={updateReport('materialLocation')} />
            </label>
            <label className="grid gap-2 text-sm text-slate-200 md:col-span-2">Describe the original work
              <textarea className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" rows="4" value={report.workDescription} maxLength="5000" required disabled={reportStatus.state === 'saving'} onChange={updateReport('workDescription')} />
            </label>
            <label className="grid gap-2 text-sm text-slate-200 md:col-span-2">Good-faith statement and requested action
              <textarea className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" rows="4" value={report.statement} maxLength="5000" required disabled={reportStatus.state === 'saving'} onChange={updateReport('statement')} />
            </label>
            <label className="grid gap-2 text-sm text-slate-200">Electronic signature
              <input className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3" value={report.signature} maxLength="200" required disabled={reportStatus.state === 'saving'} onChange={updateReport('signature')} />
            </label>
            <div className="flex items-end">
              <button className="min-h-11 rounded-xl border border-amber-300/40 bg-amber-500/15 px-5 py-3 text-sm font-semibold text-amber-100 disabled:opacity-50" type="submit" disabled={reportStatus.state === 'saving'}>Submit report</button>
            </div>
            {reportStatus.message ? <p className={`text-sm md:col-span-2 ${reportStatus.state === 'error' ? 'text-rose-300' : 'text-emerald-300'}`} role={reportStatus.state === 'error' ? 'alert' : 'status'}>{reportStatus.message}</p> : null}
          </form>
        </section>
      </div>
    </div>
  );
};

export default LegalAndSourcesPage;
